import React from 'react';
import { Box, Chip, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { croCount, projectColor, THEME } from '../utils/trackerConfig';

/**
 * The CRO table for a SMALL screen: seven number columns cannot hold readable numbers in a few hundred pixels, so each CRO becomes a small
 * card - the name (with project / team), then the numbers in a labelled grid, full size and full amounts - under a "Sort by" control.
 * It shows the same rows, in the same order, with the same totals, team bands (tap to fold), and every number still opens the rows behind it.
 * `renderValue(col, row, scope, who)` is the grid's own cell renderer, so a number looks and behaves exactly as in the table.
 */
// card spacing (px) - shared with CroGrid, which uses it to decide how many numbers go across a card without shortening any amount
export const LIST_PAD = 14; // the card's side padding
export const LIST_CELL_PAD = 4; // each number's own side padding
export const LIST_ROW_GAP = 14; // between the rows of numbers
export const LIST_VALUE_PX = 14;
// how wide (px) one number is when `perRow` of them go across a card `boxW` wide
export const listCellPx = (boxW, perRow) => (boxW - 2 * LIST_PAD) / perRow - 2 * LIST_CELL_PAD;
// n numbers in rows of at most perRow, as even as possible: 7 across 4 -> [4, 3]
export const balancedRows = (n, perRow) => {
  const rows = Math.max(1, Math.ceil(n / perRow));
  return Array.from({ length: rows }, (_, i) => Math.floor(n / rows) + (i < n % rows ? 1 : 0));
};

export default function CroList({
  columns,
  rows,
  groups,
  collapsed,
  onToggleGroup,
  sort,
  onSort,
  showProject,
  loading,
  emptyHint,
  total,
  totalScope,
  totalWho,
  renderValue,
  perRow: perRowProp,
  expanded,
  maxHeight,
}) {
  // what the "Sort by" list offers: the same things the table headers sort by
  const sortOptions = [
    ...(showProject ? [{ key: 'project', label: 'Project' }] : []),
    { key: 'name', label: 'CRO name' },
    ...(groups ? [] : [{ key: 'team', label: 'Team (CRM)' }]),
    ...columns.map((c) => ({ key: c.key, label: c.label.replace(' (₹)', '') })),
  ];
  const perRow = perRowProp || Math.min(columns.length, 4); // how many numbers go across a card (the grid picks the most that keep amounts whole)

  // the labelled numbers of one row / band / total, in balanced rows (7 numbers across 4 = 4 + 3), each row spread evenly over the card
  const sizes = balancedRows(columns.length, perRow);
  const spans = sizes.flatMap((k) => Array(k).fill(12 / k));
  const metrics = (row, scope, who) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', rowGap: `${LIST_ROW_GAP}px` }}>
      {columns.map((col, i) => (
        <Box key={col.key} sx={{ gridColumn: `span ${spans[i]}`, textAlign: 'center', minWidth: 0, px: `${LIST_CELL_PAD}px` }}>
          <Typography component="div" sx={{ fontSize: 11, lineHeight: 1.3, color: '#64748B', fontWeight: 600, mb: 0.4 }}>
            {col.short}
          </Typography>
          <Box sx={{ fontSize: LIST_VALUE_PX, lineHeight: 1.3, fontWeight: col.bold ? 800 : 700, color: Number(row[col.key]) || col.kind === 'tar' ? col.color : '#CBD5E1', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {renderValue(col, row, scope, who)}
          </Box>
        </Box>
      ))}
    </Box>
  );

  let n = 0; // running # over the visible CROs (team bands and a CRM's own line do not count)
  const croCard = (c) => {
    const i = n;
    if (!c.isCrm) n += 1;
    const pc = projectColor(c.projectKey);
    return (
      <Box
        key={`${c.userId}|${c.projectKey}`}
        sx={{ px: `${LIST_PAD}px`, py: 1.75, borderBottom: `1px solid ${THEME.grid}`, borderLeft: `4px solid ${pc.main}`, backgroundColor: c.isCrm ? THEME.primarySoft : '#fff' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography component="span" sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, minWidth: 16 }}>
            {c.isCrm ? '•' : i + 1}
          </Typography>
          <Typography component="span" sx={{ fontSize: 13.5, fontWeight: 700, color: '#1E293B' }}>
            {c.name}
          </Typography>
          {c.isCrm && <Chip label="CRM · own" size="small" sx={{ height: 18, fontSize: 11, fontWeight: 700, color: '#5B21B6', backgroundColor: '#DDD6FE' }} />}
          {!c.active && <Chip label="Inactive" size="small" sx={{ height: 18, fontSize: 11, fontWeight: 600, color: '#9A3412', backgroundColor: '#FFEDD5' }} />}
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: pc.text }}>
            {[showProject ? c.project : null, !groups ? (c.teamId === 0 ? 'No CRM' : c.teamName) : null].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        {metrics(c, { userIds: [c.userId], projectKey: c.projectKey }, c.name)}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        border: `1px solid ${THEME.grid}`,
        borderRadius: '12px',
        backgroundColor: 'white',
        boxShadow: '0 1px 6px rgba(15,23,42,0.10)',
        overflow: expanded ? 'visible' : 'hidden auto', // never sideways; inside a fixed height unless expanded
        maxHeight: expanded ? 'none' : maxHeight || 'calc(100vh - 390px)',
        opacity: loading ? 0.6 : 1,
        transition: 'opacity .15s',
      }}
    >
      {/* sort control: the table sorts by its headers; here the same sorts are one list + a direction button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1, backgroundColor: THEME.primary }}>
        <Typography component="span" sx={{ color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
          Sort by
        </Typography>
        <TextField
          select
          size="small"
          value={sortOptions.some((o) => o.key === sort.key) ? sort.key : 'name'}
          onChange={(e) => onSort(e.target.value)}
          sx={{
            flex: 1,
            backgroundColor: '#fff',
            borderRadius: '6px',
            '& .MuiInputBase-root': { fontSize: 13 },
          }}
        >
          {sortOptions.map((o) => (
            <MenuItem key={o.key} value={o.key} sx={{ fontSize: 13 }}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title={sort.dir === 'asc' ? 'Ascending - tap to reverse' : 'Descending - tap to reverse'} arrow>
          <IconButton size="small" onClick={() => onSort(sort.key)} sx={{ color: '#fff' }}>
            {sort.dir === 'asc' ? <ArrowUpwardRoundedIcon fontSize="small" /> : <ArrowDownwardRoundedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* the total of what is shown */}
      <Box sx={{ px: `${LIST_PAD}px`, py: 1.75, backgroundColor: THEME.total, borderBottom: `2px solid ${THEME.grid}` }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: THEME.primaryDark, mb: 1.5 }}>
          Total · {croCount(rows)} {croCount(rows) === 1 ? 'CRO' : 'CROs'}
        </Typography>
        {metrics(total, totalScope, totalWho || 'All CROs')}
      </Box>

      {rows.length === 0 && (
        <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center', fontSize: 13 }}>
          {emptyHint || 'No CROs match these filters.'}
        </Typography>
      )}

      {!groups && rows.map(croCard)}
      {groups &&
        groups.flatMap((g) => {
          const pc = projectColor(g.projectKey);
          const isCollapsed = collapsed && collapsed.has(g.key);
          const who = g.teamId === 0 ? 'No CRM' : `Team ${g.teamName}`;
          const band = (
            <Box
              key={`team-${g.key}`}
              onClick={() => onToggleGroup && onToggleGroup(g.key)}
              sx={{ px: `${LIST_PAD}px`, py: 1.75, cursor: 'pointer', backgroundColor: THEME.primarySoft, borderLeft: `4px solid ${pc.main}`, borderBottom: `1px solid ${THEME.grid}` }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 1.5, color: THEME.primaryDark }}>
                {isCollapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 800 }}>
                  {who}
                </Typography>
                <Chip label={`${croCount(g.rows)} ${croCount(g.rows) === 1 ? 'CRO' : 'CROs'}`} size="small" sx={{ height: 18, fontSize: 11, fontWeight: 700, color: '#fff', backgroundColor: pc.main }} />
                {showProject && (
                  <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: pc.text }}>
                    {g.project}
                  </Typography>
                )}
              </Box>
              {metrics(g.total, { userIds: g.rows.map((r) => r.userId), projectKey: g.projectKey }, who)}
            </Box>
          );
          return isCollapsed ? [band] : [band, ...g.rows.map(croCard)];
        })}
    </Box>
  );
}
